'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  startOfDay,
  format,
  getYear,
  getMonth,
} from 'date-fns';
import { MdChevronLeft, MdChevronRight, MdClose, MdDone, MdExpandMore } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  isDateDisabled,
  buildCalendarGrid,
  chunk,
  WEEKDAY_KEYS,
  MONTH_KEYS,
  CALENDAR_FRAME_HEIGHT,
} from './calendar-utils';
import type { DateRange } from './DateRangePicker.types';
import { MonthGrid } from './MonthGrid';
import { YearGrid } from './YearGrid';
import { useCalendarPopup } from './useCalendarPopup';
import { rangeDayCellClasses } from './RangeCalendarPanel.style';
import {
  calendarContainerClasses,
  calendarHeaderClasses,
  calendarSelectorButtonClasses,
  calendarWeekHeaderClasses,
  calendarWeekHeaderCellClasses,
  calendarGridClasses,
  calendarFooterClasses,
  calendarFooterNavGroupClasses,
  calendarFooterSpacerClasses,
  calendarNavButtonClasses,
  calendarDoneButtonClasses,
  calendarDesktopOverlayClasses,
  calendarDesktopDropdownClasses,
  calendarMobileBackdropClasses,
  calendarMobileDialogClasses,
  calendarMobileHeaderClasses,
  calendarMobileTitleClasses,
  calendarMobileCloseButtonClasses,
  calendarMobileBodyClasses,
} from './CalendarPanel.style';

export interface RangeCalendarPanelProps {
  open: boolean;
  onClose: () => void;
  onSelect: (range: DateRange) => void;
  selectedRange?: DateRange;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  title?: string;
  /**
   * Override the z-index of the portaled range calendar when hosted inside a high-stacking
   * surface. Same contract as `CalendarPanel.menuZIndex` — see the invariant block there
   * (PB-5106). Undefined keeps the default z-50 bands, so standalone usage is unchanged.
   *
   * No modal in the app currently embeds a DateRangePicker; this exists so the whole overlay
   * family has one uniform escape hatch, which is what stopped this defect recurring for
   * Select. Without it the next modal to embed a range picker reintroduces the bug with no
   * way for its host to fix it.
   */
  menuZIndex?: number;
}

const MOBILE_QUERY = '(max-width: 767px)';

export function RangeCalendarPanel({
  open,
  onClose,
  onSelect,
  selectedRange,
  minDate,
  maxDate,
  filterDate,
  anchorRef,
  title,
  menuZIndex,
}: RangeCalendarPanelProps) {
  const t = useTranslations('DatePicker');
  const tCommon = useTranslations('Common');
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useFocusTrap(mobileDialogRef, open && isMobile);

  const initialDate = selectedRange?.start ?? selectedRange?.end ?? new Date();
  const [rangeStart, setRangeStart] = useState<Date | null>(() => selectedRange?.start ?? null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(() => selectedRange?.end ?? null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const {
    viewDate,
    focusedDate,
    setFocusedDate,
    calendarView,
    dropdownStyle,
    panelRef,
    gridRef,
    onDayClickRef,
    handlePrevMonth,
    handleNextMonth,
    handleMonthButtonClick,
    handleYearButtonClick,
    handleMonthSelect,
    handleYearSelect,
    handleGridKeyDown,
  } = useCalendarPopup({
    open,
    isMobile,
    anchorRef,
    onClose,
    initialViewDate: initialDate,
    minDate,
    maxDate,
    filterDate,
    mobileInitialFocusRef: closeButtonRef,
    mobileDialogRef,
  });

  const handleDayClick = useCallback(
    (date: Date) => {
      if (isDateDisabled(date, minDate, maxDate, filterDate)) return;

      if (!rangeStart || (rangeStart && rangeEnd)) {
        // Start fresh selection
        setRangeStart(date);
        setRangeEnd(null);
        setHoverDate(null);
      } else {
        // We have a start, now set the end
        if (isBefore(date, rangeStart)) {
          // Clicked before start — reset start
          setRangeStart(date);
          setRangeEnd(null);
        } else {
          setRangeEnd(date);
          setHoverDate(null);
        }
      }
      setFocusedDate(date);
    },
    [rangeStart, rangeEnd, minDate, maxDate, filterDate, setFocusedDate]
  );

  // Keep the hook's ref in sync so keyboard Enter/Space triggers handleDayClick.
  useEffect(() => {
    onDayClickRef.current = handleDayClick;
  });

  const handleDayMouseEnter = useCallback(
    (date: Date) => {
      if (rangeStart && !rangeEnd) {
        setHoverDate(date);
      }
    },
    [rangeStart, rangeEnd]
  );

  const handleDone = useCallback(() => {
    onSelect({ start: rangeStart, end: rangeEnd });
    onClose();
  }, [rangeStart, rangeEnd, onSelect, onClose]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  const today = startOfDay(new Date());
  const calendarDays = buildCalendarGrid(viewDate);
  const dayRows = chunk(calendarDays, 7);
  const currentMonth = getMonth(viewDate);
  const currentYear = getYear(viewDate);
  const todayMonth = getMonth(today);
  const todayYear = getYear(today);
  const translatedMonth = t(`months.${MONTH_KEYS[currentMonth]}` as Parameters<typeof t>[0]);
  const gridLabel = `${translatedMonth} ${currentYear}`;

  // Determine the effective range end for hover preview
  const effectiveEnd = rangeEnd ?? hoverDate;

  /** Get the day cell state for range rendering. */
  function getDayState(
    date: Date
  ):
    | 'default'
    | 'today'
    | 'selected'
    | 'rangeStart'
    | 'rangeEnd'
    | 'inRange'
    | 'disabled'
    | 'adjacent' {
    const disabled = isDateDisabled(date, minDate, maxDate, filterDate);
    const isCurrentMonth = isSameMonth(date, viewDate);
    const isToday = isSameDay(date, today);

    if (disabled) return 'disabled';
    if (!isCurrentMonth) return 'adjacent';

    const isStart = rangeStart && isSameDay(date, rangeStart);
    const isEnd = effectiveEnd && isSameDay(date, effectiveEnd);

    if (rangeStart && effectiveEnd) {
      if (isStart && isEnd) return 'selected'; // Same day start/end
      if (isStart) return 'rangeStart';
      if (isEnd) return 'rangeEnd';
      if (isAfter(date, rangeStart) && isBefore(date, effectiveEnd)) return 'inRange';
    } else if (isStart) {
      return 'selected';
    }

    if (isToday) return 'today';
    return 'default';
  }

  function getDayAriaLabel(date: Date): string {
    let label = format(date, 'EEEE, MMMM d, yyyy');
    if (rangeStart && isSameDay(date, rangeStart)) {
      label += `, ${t('rangeStart')}`;
    }
    if (rangeEnd && isSameDay(date, rangeEnd)) {
      label += `, ${t('rangeEnd')}`;
    }
    return label;
  }

  const dayGridContent = (
    <>
      <div className={calendarWeekHeaderClasses} role="row" aria-hidden="true">
        {WEEKDAY_KEYS.map((dayKey) => (
          <abbr
            key={dayKey}
            title={t(`weekdaysFull.${dayKey}` as Parameters<typeof t>[0])}
            className={calendarWeekHeaderCellClasses}
          >
            {t(`weekdays.${dayKey}` as Parameters<typeof t>[0])}
          </abbr>
        ))}
      </div>

      <div
        ref={gridRef}
        className={calendarGridClasses}
        role="grid"
        aria-label={gridLabel}
        onKeyDown={handleGridKeyDown}
      >
        {dayRows.map((row, rowIdx) => (
          <div key={rowIdx} role="row" className="tw:contents">
            {row.map((date) => {
              const dateKey = date.toISOString();
              const disabled = isDateDisabled(date, minDate, maxDate, filterDate);
              const state = getDayState(date);
              const isFocused = isSameDay(date, focusedDate);
              const isSelected =
                state === 'selected' || state === 'rangeStart' || state === 'rangeEnd';

              const isToday = isSameDay(date, today);
              return (
                <button
                  key={dateKey}
                  type="button"
                  role="gridcell"
                  aria-selected={isSelected}
                  aria-disabled={disabled || undefined}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={getDayAriaLabel(date)}
                  tabIndex={isFocused ? 0 : -1}
                  data-cy="range-calendar-day"
                  onClick={disabled ? undefined : () => handleDayClick(date)}
                  onMouseEnter={() => handleDayMouseEnter(date)}
                  className={rangeDayCellClasses({ state })}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );

  let bodyContent: ReactNode;
  switch (calendarView) {
    case 'months':
      bodyContent = (
        <MonthGrid
          selectedMonth={currentMonth}
          currentMonth={todayMonth}
          onSelect={handleMonthSelect}
        />
      );
      break;
    case 'years':
      bodyContent = (
        <YearGrid
          selectedYear={currentYear}
          currentYear={todayYear}
          onSelect={handleYearSelect}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    default:
      bodyContent = dayGridContent;
  }

  const rangeStatusText = rangeStart
    ? rangeEnd
      ? t('selectedRange', { start: format(rangeStart, 'MMM d'), end: format(rangeEnd, 'MMM d') })
      : t('startSelectEnd', { date: format(rangeStart, 'MMM d') })
    : t('selectStartDate');

  // Desktop popup height: cap to the placed space (up to the 395px frame) so the
  // grid scrolls instead of clipping off-screen (PB-2407). Mobile uses the bottom sheet.
  const desktopFrameStyle = isMobile
    ? undefined
    : { height: `min(${CALENDAR_FRAME_HEIGHT}px, ${dropdownStyle.maxHeight || CALENDAR_FRAME_HEIGHT}px)` };

  const calendarContent = (
    <div
      className={calendarContainerClasses}
      // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed desktop frame height)
      style={desktopFrameStyle}
      role="group"
      aria-label={title ?? t('dateRangePicker')}
    >
      {/* Month/Year header */}
      <div className={calendarHeaderClasses}>
        <button
          type="button"
          onClick={handleMonthButtonClick}
          className={calendarSelectorButtonClasses({ active: calendarView === 'months' })}
          aria-label={t('selectMonthCurrent', { month: translatedMonth })}
          aria-expanded={calendarView === 'months'}
          data-cy="range-calendar-month-button"
        >
          {translatedMonth}
          <MdExpandMore className="tw:h-4 tw:w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={handleYearButtonClick}
          className={calendarSelectorButtonClasses({ active: calendarView === 'years' })}
          aria-label={t('selectYearCurrent', { year: String(currentYear) })}
          aria-expanded={calendarView === 'years'}
          data-cy="range-calendar-year-button"
        >
          {currentYear}
          <MdExpandMore className="tw:h-4 tw:w-4" aria-hidden="true" />
        </button>
      </div>

      {bodyContent}

      {/* Live region */}
      <div aria-live="polite" className="tw:sr-only">
        {rangeStatusText}
      </div>

      {/* Footer */}
      {calendarView === 'days' && (
        <div className={calendarFooterClasses}>
          <div className={calendarFooterNavGroupClasses}>
            <button
              type="button"
              onClick={handlePrevMonth}
              className={calendarNavButtonClasses}
              aria-label={t('previousMonth')}
              data-cy="range-calendar-prev-month"
            >
              <MdChevronLeft className="tw:h-5 tw:w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className={calendarNavButtonClasses}
              aria-label={t('nextMonth')}
              data-cy="range-calendar-next-month"
            >
              <MdChevronRight className="tw:h-5 tw:w-5" aria-hidden="true" />
            </button>
          </div>
          <div className={calendarFooterSpacerClasses} />
          <button
            type="button"
            onClick={handleDone}
            className={calendarDoneButtonClasses}
            aria-label={t('confirmRangeSelection')}
            data-cy="range-calendar-done"
          >
            <MdDone className="tw:h-5 tw:w-5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );

  let content: ReactNode;

  if (isMobile) {
    content = (
      <>
        <div
          className={calendarMobileBackdropClasses}
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
          aria-hidden="true"
          onClick={handleBackdropClick}
          data-cy="range-calendar-backdrop"
        />
        <div
          ref={mobileDialogRef}
          className={calendarMobileDialogClasses}
          style={menuZIndex != null ? { zIndex: menuZIndex } : undefined}
          role="dialog"
          aria-modal="true"
          aria-label={title ?? t('selectDateRange')}
          data-cy="range-calendar-panel"
        >
          <div className={calendarMobileHeaderClasses}>
            {title != null && <h2 className={calendarMobileTitleClasses}>{title}</h2>}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label={tCommon('close')}
              className={calendarMobileCloseButtonClasses}
              data-cy="range-calendar-close"
            >
              <MdClose className="tw:h-5 tw:w-5" aria-hidden="true" />
            </button>
          </div>
          <div className={calendarMobileBodyClasses}>{calendarContent}</div>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <div
          className={calendarDesktopOverlayClasses}
          aria-hidden="true"
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
        />
        <div
          ref={panelRef}
          className={calendarDesktopDropdownClasses}
          data-cy="range-calendar-panel"
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed dropdown position/width)
          style={{
            position: 'fixed',
            ...(menuZIndex != null && { zIndex: menuZIndex }),
            top: dropdownStyle.top,
            bottom: dropdownStyle.bottom,
            left: dropdownStyle.left,
            minWidth: dropdownStyle.width,
          }}
        >
          {calendarContent}
        </div>
      </>
    );
  }

  return createPortal(content, document.body);
}
